import { Component, h, clamp, register } from './core.js';
import { renderIcon } from './icons.js';

/**
 * Stepper: numbered steps for a wizard. Owns the step indicator only; the
 * host attaches its own content for each step.
 *
 * new dfc.Stepper(container, {
 *   steps: [{ id, label, icon, description, optional }],   // icon: any icon spec (built-in name, SVG, URL, { className })
 *   active: 0, linear: true, clickable: true, orientation: 'horizontal' | 'vertical',
 *   showLabels: true, validate: null | (from, to) => boolean | Promise<boolean>
 * })
 * Events: beforeChange { from, to, cancel() }, valueChanged { value, previous, active, source }
 * Methods: next(), prev(), goTo(i), setStatus(i, 'pending'|'complete'|'error'), activeStep (get/set)
 */
export class Stepper extends Component {
	static get componentName() { return 'stepper'; }

	defaults() {
		return { steps: [], active: 0, value: undefined, linear: true, clickable: true, orientation: 'horizontal', showLabels: true, validate: null };
	}

	render() {
		const o = this.options;
		this._status = this._status || [];
		if (o.value !== undefined && o.value !== null && !this._valueApplied) { o.active = Number(o.value) || 0; this._valueApplied = true; }
		this._active = clamp(Number(o.active) || 0, 0, Math.max(0, o.steps.length - 1));
		this._furthest = Math.max(this._furthest || 0, this._active);
		this.el.className = `dfc dfc-stepper dfc-${o.orientation}` + (o.showLabels ? '' : ' no-labels') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		this.listEl = h('ol', { className: 'dfc-stepper-list', role: 'tablist', 'aria-orientation': o.orientation });
		this.stepEls = o.steps.map((step, i) => {
			const status = this._statusOf(i);
			const marker = h('span', { className: 'dfc-stepper-marker', 'aria-hidden': 'true' },
				status === 'complete' ? renderIcon('check')
					: status === 'error' ? renderIcon('exclamation')
						: (step.icon || step.iconCss) ? renderIcon(step.icon || { className: step.iconCss }) : String(i + 1));
			const label = h('span', { className: 'dfc-stepper-label' },
				h('span', { className: 'dfc-stepper-title', text: step.label || `Step ${i + 1}` }),
				step.description ? h('span', { className: 'dfc-stepper-desc', text: step.description }) : null,
				step.optional ? h('span', { className: 'dfc-stepper-optional', text: 'Optional' }) : null);
			const btn = h('button', {
				type: 'button', className: 'dfc-stepper-step', role: 'tab', dataset: { index: String(i) },
				'aria-selected': i === this._active ? 'true' : 'false', 'aria-current': i === this._active ? 'step' : null,
				'aria-disabled': this._reachable(i) ? null : 'true', tabindex: i === this._active ? '0' : '-1', title: step.label || null,
			}, marker, label);
			const li = h('li', { className: `dfc-stepper-item is-${status}` + (i === this._active ? ' is-active' : '') + (this._reachable(i) ? '' : ' is-locked') }, btn);
			this.listEl.appendChild(li);
			return li;
		});
		this.el.appendChild(this.listEl);
	}

	bind() {
		this._onClick = (e) => {
			const btn = e.target.closest && e.target.closest('.dfc-stepper-step');
			if (!btn || !this.el.contains(btn) || !this.options.clickable) return;
			this.goTo(Number(btn.dataset.index), 'user');
		};
		this._onKey = (e) => {
			const btn = e.target.closest && e.target.closest('.dfc-stepper-step');
			if (!btn) return;
			const n = this.options.steps.length;
			let i = Number(btn.dataset.index);
			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') i = (i + 1) % n;
			else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') i = (i - 1 + n) % n;
			else if (e.key === 'Home') i = 0;
			else if (e.key === 'End') i = n - 1;
			else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.goTo(i, 'user'); return; }
			else return;
			e.preventDefault();
			const target = this.stepEls[i] && this.stepEls[i].querySelector('.dfc-stepper-step');
			if (target) target.focus();
		};
		this.el.addEventListener('click', this._onClick);
		this.el.addEventListener('keydown', this._onKey);
	}

	unbind() {
		if (!this.el) return;
		this.el.removeEventListener('click', this._onClick);
		this.el.removeEventListener('keydown', this._onKey);
	}

	get activeStep() { return this._active; }
	set activeStep(i) { this.goTo(i, 'api'); }
	/** value = the active step index. Setting it moves synchronously past validation only when silent; otherwise it is goTo(). */
	get value() { return this._active; }
	set value(i) { this.goTo(i, 'api'); }
	get steps() { return this.options.steps; }

	next() { return this.goTo(this._active + 1, 'api'); }
	prev() { return this.goTo(this._active - 1, 'api'); }

	/** Moves to step i. Returns a Promise<boolean> resolving true when the move happened. */
	async goTo(i, source = 'api') {
		const to = clamp(Number(i) || 0, 0, this.options.steps.length - 1);
		const from = this._active;
		if (to === from) return false;
		if (source === 'user' && !this._reachable(to)) return false;
		let cancelled = false;
		this.emit('beforeChange', { from, to, source, cancel: () => { cancelled = true; } });
		if (cancelled) return false;
		if (typeof this.options.validate === 'function') {
			const ok = await this.options.validate(from, to);
			if (ok === false) return false;
		}
		if (to > from && this._statusOf(from) !== 'error') this._status[from] = 'complete';
		this._active = to;
		this.options.active = to;
		this._furthest = Math.max(this._furthest, to);
		this.render();
		this._valueChanged(to, from, source, { active: to });
		return true;
	}

	setStatus(i, status) {
		this._status[i] = status;
		this.render();
		return this;
	}

	_statusOf(i) {
		if (this._status[i]) return this._status[i];
		return i < this._active ? 'complete' : 'pending';
	}

	_reachable(i) {
		if (!this.options.linear) return true;
		return i <= this._furthest + 1 && i <= Math.max(this._active, this._furthest) + 1;
	}
}

register('stepper', Stepper);
